import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { SEOHead } from '../components/SEOHead';
import { getSaveaPhone, getSaveaPhoneTel, getSpecialistMessage } from '../config/phoneConfig';
import { PROVIDERS_DATA } from '../config/providersData';
import { StickyPhoneBar } from '../components/StickyPhoneBar';
import { 
  Phone, 
  Zap,
  Home,
  TrendingDown,
  Clock,
  ChevronRight,
  Leaf,
  Shield
} from 'lucide-react';

export const EDFPageV2 = () => {
  const { i18n } = useTranslation();
  const [competitors, setCompetitors] = useState([]);
  
  const country = 'FR';
  const vertical = 'electricity';
  const specialist = getSpecialistMessage(country, vertical);
  
  // LP = CTA principal (0970252402), SB = Souscrire (0564098904)
  const saveaPhone = getSaveaPhone(country, vertical, 'LP');
  const saveaPhoneTel = getSaveaPhoneTel(country, vertical, 'LP');
  const souscrirePhone = getSaveaPhone(country, vertical, 'SB');
  const souscrirePhoneTel = getSaveaPhoneTel(country, vertical, 'SB');

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const now = new Date();
  const currentMonth = `${monthNames[now.getMonth()]} ${now.getFullYear()}`;

  const provider = {
    name: 'EDF',
    logo: '/images/logos/FAzUgTB.png',
    color: '#07387A',
    price: '0.1940€/kWh'
  };

  // Logos concurrents pour le hero (3 gros fournisseurs - pas EDF)
  const bottomLogos = [
    { src: '/images/logos/FAzUZYv.png', alt: 'Engie' },
    { src: '/images/logos/FAzg1MF.png', alt: 'TotalEnergies' },
    { src: '/images/logos/FAzgJGn.png', alt: 'Enedis' },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const frNrjProviders = Object.values(PROVIDERS_DATA).filter(
      p => p.country === 'FR' && p.vertical === 'electricity' && p.name !== 'EDF'
    );
    const shuffled = frNrjProviders.sort(() => 0.5 - Math.random());
    setCompetitors(shuffled.slice(0, 8));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Comparer les offres EDF - Numéro gratuit | Savea.info"
        description="Comparez les offres EDF avec nos partenaires. Tarif réglementé 0.1940€/kWh. Service gratuit et sans engagement. Conseillers disponibles 8h-21h."
        keywords="EDF, comparer EDF, offres EDF, tarif EDF, electricite EDF, fournisseur electricite"
        type="provider"
      />
      
      {/* Schema.org LocalBusiness + FAQPage */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "LocalBusiness",
            "name": "SAVEA - Comparateur EDF",
            "description": "Service gratuit de comparaison des offres EDF avec les fournisseurs alternatifs",
            "telephone": "+33970252402",
            "url": "https://savea.info/fr/fournisseur/electricite-edf",
            "areaServed": "France",
            "priceRange": "Gratuit",
            "openingHours": "Mo-Sa 08:00-21:00"
          },
          {
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Quel est le tarif EDF en 2026 ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Le tarif réglementé EDF (Tarif Bleu) est de 0.1940€/kWh en option Base et 0.2065€/kWh en Heures Pleines."
                }
              },
              {
                "@type": "Question", 
                "name": "Peut-on trouver moins cher qu'EDF ?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Oui, des fournisseurs alternatifs proposent des offres jusqu'à 15% moins chères que le tarif réglementé EDF."
                }
              }
            ]
          }
        ]
      })}} />
      
      {/* HERO - Design dark premium comme comparateur */}
      <section className="pt-6 pb-10 lg:pt-12 lg:pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="max-w-lg lg:max-w-6xl mx-auto px-4">
          
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
            <div>
              {/* SAVEA Branding */}
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-4">
                <div className="w-7 h-7 lg:w-9 lg:h-9 bg-gradient-to-br from-sage-600 to-sage-700 rounded-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
                </div>
                <span className="text-white font-bold text-lg lg:text-xl">SAVEA</span>
              </div>

              {/* Badge */}
              <div className="flex justify-center lg:justify-start mb-4">
                <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-1.5">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <span className="text-blue-300 text-xs font-medium">Service gratuit et sans engagement</span>
                </div>
              </div>
              
              {/* Titre */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 text-center lg:text-left leading-tight text-white">
                Comment nous contacter ?
              </h1>
              
              <p className="text-slate-300 text-sm lg:text-base text-center lg:text-left mb-6">
                Appelez-nous pour comparer les offres d'EDF avec celles de nos partenaires. 
                Vous pourriez réduire votre facture d'électricité.
              </p>
              
              {/* Logo principal + logos secondaires - fond blanc */}
              <div className="bg-white rounded-2xl p-4 border border-white/30 mb-3 lg:mb-0 overflow-hidden lg:max-w-sm">
                <div className="flex flex-col items-center gap-3">
                  <img 
                    src={provider.logo}
                    alt="EDF"
                    className="h-14 w-auto max-w-[160px] object-contain"
                    loading="eager"
                  />
                  <div className="flex items-center justify-center gap-4 w-full">
                    {bottomLogos.map((logo, index) => (
                      <img 
                        key={index}
                        src={logo.src}
                        alt={logo.alt}
                        className="h-6 w-auto max-w-[70px] object-contain opacity-60"
                        loading="eager"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div>
              {/* Disclaimer light entre logos et CTA */}
              <p className="text-slate-400/70 text-xs text-center lg:text-left mb-3">
                SAVEA est un service de comparaison d'offres d'énergie gratuit. Les tarifs affichés sont fournis à titre indicatif et peuvent varier. Pour obtenir un devis personnalisé, contactez nos conseillers. Service gratuit - Appel non surtaxé.
              </p>

              {/* CTA Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 lg:p-8 border border-white/20">
                
                {/* Numéro principal */}
                <a 
                  href={`tel:${saveaPhoneTel}`}
                  className="w-full flex flex-col items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg lg:text-xl py-4 lg:py-5 rounded-xl shadow-lg shadow-blue-500/30 transition-all duration-300 mb-4"
                  data-testid="main-cta-phone"
                >
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span className="savea-phone-number">{saveaPhone}</span>
                  </div>
                  <span className="text-blue-200 text-xs mt-1">Numéro Gratuit</span>
                </a>

                {/* Boutons secondaires */}
                <div className="grid grid-cols-2 gap-3">
                  <a 
                    href={`tel:${souscrirePhoneTel}`}
                    className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 lg:py-4 px-4 rounded-xl transition-all duration-300 text-sm border border-white/10"
                    data-testid="cta-souscrire"
                    data-dest="0564098904"
                  >
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span>Souscrire</span>
                  </a>
                  <a 
                    href={`tel:${saveaPhoneTel}`}
                    className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 lg:py-4 px-4 rounded-xl transition-all duration-300 text-sm border border-white/10"
                    data-testid="cta-demenagement"
                    data-dest="0970252402"
                  >
                    <Home className="w-4 h-4 text-blue-400" />
                    <span>Déménagement</span>
                  </a>
                </div>

                {/* Horaires */}
                <div className="text-center mt-4 text-slate-300 text-xs">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Lundi - Samedi • 8h-21h
                  </span>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </section>

      {/* Numéro direct EDF */}
      <section className="py-4 bg-blue-50 border-y border-blue-100">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-center text-gray-700 text-sm">
            Numéro direct EDF : <span className="font-semibold text-blue-800">3404</span> <span className="text-gray-500 text-xs">(appel non surtaxé)</span>
          </p>
        </div>
      </section>

      {/* Tarifs EDF */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
            Tarifs EDF - {currentMonth}
          </h2>
          <p className="text-gray-500 text-sm text-center mb-6">
            Prix du kWh en option Base et Heures Creuses
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-gray-900">Tarif Bleu - Base</h3>
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">TRV</span>
                </div>
                <div className="text-3xl font-bold text-blue-700 mb-2">0.1940€<span className="text-lg text-gray-500">/kWh</span></div>
                <p className="text-gray-500 text-sm">Abonnement : 15,65€/mois (6 kVA)</p>
              </CardContent>
            </Card>
            
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-gray-900">Tarif Bleu - HP/HC</h3>
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">TRV</span>
                </div>
                <div className="mb-2">
                  <span className="text-xl font-bold text-blue-700">HP: 0.2065€</span>
                  <span className="text-gray-500 text-sm"> / </span>
                  <span className="text-xl font-bold text-emerald-600">HC: 0.1579€</span>
                </div>
                <p className="text-gray-500 text-sm">Abonnement : 16,30€/mois (6 kVA)</p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50/30">
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="font-semibold text-gray-900">Vert Électrique</h3>
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded flex items-center gap-1">
                    <Leaf className="w-3 h-3" /> Verte
                  </span>
                </div>
                <div className="text-3xl font-bold text-green-600 mb-2">0.1860€<span className="text-lg text-gray-500">/kWh</span></div>
                <p className="text-gray-500 text-sm">100% renouvelable • -4% vs TRV</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparer avec d'autres fournisseurs */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
            Comparez avec d'autres fournisseurs
          </h2>
          <p className="text-gray-500 text-sm text-center mb-6">
            Nos conseillers peuvent vous présenter ces alternatives
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {competitors.map((comp, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between bg-gray-50 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={comp.logo_url}
                      alt={comp.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{comp.name}</h3>
                    <p className="text-xs text-gray-500">
                      {comp.offers?.[0]?.green ? '🌱 Offre verte' : 'Offre classique'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-emerald-600">{comp.main_price}</div>
                  <div className="text-xs text-gray-400">TTC/kWh</div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA */}
          <div className="text-center mt-8">
            <a 
              href={`tel:${saveaPhoneTel}`}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full transition-all"
            >
              <Phone className="w-4 h-4" />
              <span>Comparer avec un conseiller</span>
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* À propos d'EDF */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            À propos d'EDF
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Fournisseur historique</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  EDF est le fournisseur historique d'électricité en France depuis 1946. 
                  Leader du marché avec près de <strong>28 millions de clients</strong>, 
                  EDF propose le tarif réglementé Bleu ainsi que des offres de marché 
                  comme Vert Électrique.
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingDown className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Peut-on faire moins cher ?</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Oui ! Des fournisseurs alternatifs proposent des offres jusqu'à 
                  <strong> 15% moins chères</strong> que le TRV d'EDF. En changeant de fournisseur, 
                  un foyer moyen peut économiser entre <strong>100€ et 200€ par an</strong> 
                  sans changer ses habitudes.
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Énergie décarbonée</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  EDF produit près de <strong>90% d'électricité décarbonée</strong> grâce 
                  au nucléaire et aux énergies renouvelables (hydraulique, éolien, solaire). 
                  L'offre Vert Électrique garantit une électricité 100% renouvelable.
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Changer, c'est simple</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Changer de fournisseur est <strong>gratuit, sans coupure et sans intervention</strong>. 
                  Votre nouveau fournisseur s'occupe de toutes les démarches de résiliation. 
                  Vous gardez le même compteur et la même qualité d'électricité.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            Questions fréquentes
          </h2>
          
          <div className="space-y-4">
            <details className="bg-gray-50 rounded-xl p-4 cursor-pointer group">
              <summary className="font-semibold text-gray-900 flex items-center justify-between">
                Qu'est-ce que le tarif réglementé EDF ?
                <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-gray-600 text-sm mt-3">
                Le tarif réglementé de vente (TRV) est fixé par les pouvoirs publics et proposé uniquement par EDF. 
                Il sert de référence pour évaluer les offres des autres fournisseurs.
              </p>
            </details>
            
            <details className="bg-gray-50 rounded-xl p-4 cursor-pointer group">
              <summary className="font-semibold text-gray-900 flex items-center justify-between">
                Peut-on trouver moins cher qu'EDF ?
                <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-gray-600 text-sm mt-3">
                Oui, des fournisseurs alternatifs proposent des offres jusqu'à 15% moins chères que le TRV d'EDF. 
                Nos conseillers peuvent vous aider à trouver l'offre la plus adaptée à votre profil.
              </p>
            </details>
            
            <details className="bg-gray-50 rounded-xl p-4 cursor-pointer group">
              <summary className="font-semibold text-gray-900 flex items-center justify-between">
                Changer de fournisseur, est-ce compliqué ?
                <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="text-gray-600 text-sm mt-3">
                Non, c'est gratuit, sans coupure et sans intervention technique. 
                Votre nouveau fournisseur s'occupe de toutes les démarches de résiliation.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="max-w-lg mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-3">
            Besoin d'un conseil personnalisé ?
          </h2>
          <p className="text-slate-300 text-sm mb-6">
            Nos conseillers comparent les offres EDF avec celles de nos partenaires
          </p>
          <a 
            href={`tel:${saveaPhoneTel}`}
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-full shadow-lg transition-all hover:shadow-xl"
          >
            <Phone className="w-5 h-5" />
            <span className="savea-phone-number">{saveaPhone}</span>
          </a>
          <p className="text-slate-400 text-xs mt-3">Numéro Gratuit</p>
        </div>
      </section>

      {/* Footer Compliance */}
      {/* Footer Compliance */}
      <section className="py-6 pb-20 bg-gray-100 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-gray-500 text-xs text-center leading-relaxed">
            SAVEA est un service de comparaison d'offres d'énergie gratuit. 
            Les tarifs affichés sont fournis à titre indicatif et peuvent varier. 
            Pour obtenir un devis personnalisé, contactez nos conseillers. 
            Service gratuit - Appel non surtaxé.
          </p>
        </div>
      </section>

      {/* Sticky Phone Bar - Couleur EDF bleu */}
      <StickyPhoneBar providerColor="bg-blue-700 hover:bg-blue-800" />
    </div>
  );
};

export default EDFPageV2;
